import { 
    auth, 
    db, 
    doc, 
    getDoc, 
    setDoc, 
    updateDoc, 
    serverTimestamp,
    signInWithEmailAndPassword,
    signOut as firebaseSignOut,
    setPersistence,
    browserLocalPersistence,
    browserSessionPersistence
} from '../../config/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';

class AuthService {
    
    async loginWithEmail(email, password, rememberMe = false) {
        if (!email || !password) {
            throw new Error('Email and password are required');
        }

        try {
            const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
            await setPersistence(auth, persistence);

            const userCredential = await signInWithEmailAndPassword(auth, String(email || '').trim().toLowerCase(), password);
            const user = userCredential.user;

            // Administrator identity must be explicit. Never manufacture an
            // admin role when Firestore is missing or temporarily unavailable.
            let userData = null;
            try {
                const userDoc = await Promise.race([
                    getDoc(doc(db, 'users', user.uid)),
                    new Promise((_, reject) =>
                        setTimeout(() => reject(new Error('Administrator profile lookup timed out')), 5000)
                    )
                ]);
                if (!userDoc || !userDoc.exists()) {
                    await firebaseSignOut(auth);
                    throw new Error('Administrator profile not found. Access denied.');
                }
                userData = userDoc.data() || {};
                const role = String(userData.role || userData.userRole || '').toLowerCase().replace(/[\s-]+/g, '_');
                if (!['super_admin', 'superadmin'].includes(role) || userData.isActive === false || userData.isBlocked === true) {
                    await firebaseSignOut(auth);
                    throw new Error('This account is not authorised for the TELCOFAIR Admin Portal.');
                }
            } catch (err) {
                if (auth.currentUser) {
                    try {
                        await firebaseSignOut(auth);
                    } catch (signOutError) {
                        console.warn('Failed to clear rejected admin session:', signOutError);
                    }
                }
                throw err;
            }

            // Update last login (non-blocking)
            try {
                await updateDoc(doc(db, 'users', user.uid), {
                    lastActive: serverTimestamp(),
                    isOnline: true,
                    'security.lastLogin': serverTimestamp()
                });
            } catch (err) {
                console.log('Login update skipped');
            }

            return {
                success: true,
                user: user,
                userData: userData
            };

        } catch (error) {
            console.error('Login error:', error);
            throw new Error(this.getErrorMessage(error));
        }
    }

    async logout() {
        try {
            const user = auth.currentUser;
            if (user) {
                try {
                    await updateDoc(doc(db, 'users', user.uid), {
                        isOnline: false,
                        lastActive: serverTimestamp()
                    });
                } catch (err) {
                    console.log('Logout update skipped');
                }
            }
            await firebaseSignOut(auth);
            return { success: true };
        } catch (error) {
            console.error('Logout error:', error);
            throw error;
        }
    }


    async resetPassword(email) {
        const normalizedEmail = String(email || '').trim().toLowerCase();
        if (!normalizedEmail) throw new Error('Email is required');
        try {
            await sendPasswordResetEmail(auth, normalizedEmail);
            // Keep the response enumeration-safe: callers should not reveal whether
            // an administrator account exists for a supplied address.
            return { success: true, message: 'If an account exists, a password reset email has been sent.' };
        } catch (error) {
            if (String(error?.code || '').toLowerCase() === 'auth/user-not-found') {
                return { success: true, message: 'If an account exists, a password reset email has been sent.' };
            }
            throw new Error(this.getErrorMessage(error));
        }
    }

    async validateSession() {
        try {
            const user = auth.currentUser;
            if (!user) {
                return { valid: false };
            }
            return { valid: true, user: user };
        } catch (error) {
            return { valid: false };
        }
    }

    getErrorMessage(error) {
        switch (error.code) {
            case 'auth/user-not-found':
                return 'No account found with this email';
            case 'auth/wrong-password':
                return 'Incorrect password';
            case 'auth/invalid-email':
                return 'Invalid email address';
            case 'auth/too-many-requests':
                return 'Too many failed attempts. Please try again later';
            case 'auth/network-request-failed':
                return 'Network error. Please check your connection';
            default:
                return error.message || 'Login failed. Please try again';
        }
    }
}

export default new AuthService();